/**
 * La page d'erreur — ce que voit quelqu'un quand un écran casse.
 *
 * **Elle n'existait pas**, et son absence avait une conséquence précise : une
 * exception non rattrapée dans un composant fait démonter tout l'arbre React, et
 * l'utilisateur se retrouve devant une page **blanche**. Pas un message, pas un
 * lien, rien à cliquer — l'un des rares états dont on ne sort qu'en retapant une
 * URL, et dont personne ne fait de rapport de bug parce qu'il n'y a rien à
 * décrire.
 *
 * Quatre choix, et chacun répond à une question que la page blanche laissait
 * sans réponse :
 *
 * - **« Réessayer » avant « Retour à l'accueil ».** La plupart de ces erreurs
 *   sont passagères — une réponse mal formée, une coupure réseau — et
 *   `reset()` du routeur remonte l'écran sans perdre la place où l'on était.
 *   Renvoyer d'abord à l'accueil ferait recommencer un parcours pour un incident
 *   d'une seconde.
 * - **Le détail technique est affiché, replié.** Un message d'erreur brut n'est
 *   pas destiné à l'utilisateur (docs/08 §6) ; il est destiné à celui à qui il
 *   va le lire au téléphone. Le cacher entièrement rend le support aveugle, le
 *   montrer d'emblée fait peur.
 * - **`role="alert"`.** Un écran qui se remplace par une erreur doit être
 *   annoncé : sans cela, un lecteur d'écran continue de lire la page d'avant.
 * - **Aucune traduction du message technique.** Le code stable est ce que le
 *   frontend traduit (docs/22 §5) ; ici, il n'y a pas de code — c'est une
 *   exception JavaScript, et la traduire lui donnerait l'air d'un message
 *   métier.
 */
import { Link, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/Button";

interface Props {
  error: Error;
}

export function ErrorPage({ error }: Props) {
  const { t } = useTranslation();
  const router = useRouter();

  return (
    <div className="mx-auto max-w-xl space-y-6 py-12 text-center" role="alert">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">{t("error.heading")}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t("error.body")}</p>
      </div>

      <div className="flex flex-wrap justify-center gap-3">
        <Button
          type="button"
          onClick={() => {
            void router.invalidate();
          }}
        >
          {t("error.retry")}
        </Button>
        <Link to="/" className="text-brand-600 inline-flex items-center underline">
          {t("notFound.backHome")}
        </Link>
      </div>

      {error.message !== "" && (
        <details className="text-left">
          <summary className="cursor-pointer text-sm text-slate-500 dark:text-slate-400">
            {t("error.details")}
          </summary>
          <pre className="mt-2 overflow-x-auto rounded bg-slate-100 p-3 text-xs whitespace-pre-wrap dark:bg-slate-800">
            {error.message}
          </pre>
        </details>
      )}
    </div>
  );
}
